import React, { useEffect, useState } from "react";
import client from "@/api/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, ClipboardList, Trash2 } from "lucide-react";

const ACTION_COLORS = {
  upload_file: "#2563EB", run_execution: "#006080", create_api_def: "#7C3AED",
  delete: "#DC2626", login: "#059669", create_company: "#D97706",
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [resourceType, setResourceType] = useState("__all__");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => { load(); }, [page, resourceType]);

  const load = async () => {
    setLoading(true);
    const params = { page, per_page: 50 };
    if (resourceType && resourceType !== "__all__") params.resource_type = resourceType;
    if (search) params.action = search;
    const res = await client.get("/audit/", { params }).catch(() => ({ data: { logs: [], total: 0 } }));
    setLogs(res.data.logs || []);
    setTotal(res.data.total || 0);
    setLoading(false);
  };

  const totalPages = Math.max(1, Math.ceil(total / 50));

  return (
    <div className="p-6 max-w-6xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Audit Log</h1>
          <p className="text-gray-500 text-sm">Track all user actions across the platform</p>
        </div>
        {selected.size > 0 && (
          <Button variant="outline" className="text-red-500 hover:bg-red-50 hover:border-red-200" onClick={async () => {
            if (!window.confirm(`Delete ${selected.size} log entries?`)) return;
            setBulkDeleting(true);
            try { await client.post("/audit/bulk-delete", { ids: [...selected] }); setSelected(new Set()); load(); }
            finally { setBulkDeleting(false); }
          }} disabled={bulkDeleting}>
            <Trash2 size={14} className="mr-1.5" />{bulkDeleting ? "Deleting..." : `Delete ${selected.size}`}
          </Button>
        )}
      </div>
      <div className="flex gap-3 mb-4">
        <Input className="w-64" placeholder="Filter by action..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} onKeyDown={e => e.key === "Enter" && load()} />
        <Select value={resourceType} onValueChange={v => { setResourceType(v); setPage(1); }}>
          <SelectTrigger className="w-44"><SelectValue placeholder="All resources" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All resources</SelectItem>
            <SelectItem value="upload">Uploads</SelectItem>
            <SelectItem value="execution">Executions</SelectItem>
            <SelectItem value="api_definition">API Definitions</SelectItem>
            <SelectItem value="template">Templates</SelectItem>
            <SelectItem value="user">Users</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load}>Search</Button>
      </div>
      {loading ? (
        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-white border border-gray-200 rounded animate-pulse" />)}</div>
      ) : logs.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
          <ClipboardList size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">No audit logs found</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full data-table text-xs">
              <thead><tr>
                <th className="px-3 py-2">Time</th>
                <th className="px-3 py-2">User</th>
                <th className="px-3 py-2">Action</th>
                <th className="px-3 py-2">Resource</th>
                <th className="px-3 py-2">IP</th>
              </tr></thead>
              <tbody>
                {logs.map(l => (
                  <tr key={l._id}>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-400">{l.created_at ? new Date(l.created_at).toLocaleString() : "–"}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{l.user_email}</div>
                      <div className="text-gray-400 capitalize">{l.role?.replace(/_/g, " ")}</div>
                    </td>
                    <td className="px-3 py-2">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: `${ACTION_COLORS[l.action] || "#6B7280"}20`, color: ACTION_COLORS[l.action] || "#6B7280" }}>
                        {l.action}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-500">
                      {l.resource_type && <span className="font-mono">{l.resource_type}</span>}
                      {l.resource_id && <span className="text-gray-300 ml-1">#{l.resource_id.slice(-6)}</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-gray-400">{l.ip_address}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-400">{total} total entries</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" className="h-6 text-xs" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={12} /></Button>
              <span className="text-xs text-gray-500 self-center px-2">{page}/{totalPages}</span>
              <Button size="sm" variant="outline" className="h-6 text-xs" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight size={12} /></Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
